import { KEYS } from 'platejs';

// Element types we intentionally do NOT allow the block drag handle to drag.
// Tables and the column-layout containers (column_group / column) are
// structurally complex and guarded by dedicated normalizers in
// usePlateEditorLogic.tsx (table-boundary delete guard, inter-void paragraph
// insertion, nested column_group flattening). Moving them as whole nodes via
// the dnd plugin's moveNodes can fire those normalizers mid-move and corrupt
// the layout, so we skip the handle for them. They remain valid DROP targets
// (a paragraph can still be dropped above/below a table) — only DRAGGING them
// is disabled.
//
// tr / td / th / column are nested children, never top-level, so the handle
// would never appear on them anyway; listed for completeness/clarity.
const NON_DRAGGABLE_TYPES = new Set<string>(
    [(KEYS as any).table, (KEYS as any).tr, (KEYS as any).td, (KEYS as any).th, (KEYS as any).column, (KEYS as any).columnGroup].filter(
        Boolean,
    ),
);

/**
 * A block is draggable when:
 *   - it has a stable `id` (required — @platejs/dnd identifies the drag source
 *     by node id; NodeIdPlugin assigns one to every block), and
 *   - its type is not in NON_DRAGGABLE_TYPES (tables / column layout).
 *
 * Blocks without an `id` (e.g. transiently before NodeIdPlugin normalizes, or
 * inline/text leaves which never get one) are not draggable — returning false
 * makes BlockDraggable render the children untouched.
 */
export function isDraggableBlock(element: any): boolean {
    if (!element || typeof element.id !== 'string' || !element.id) return false;
    if (element.type && NON_DRAGGABLE_TYPES.has(element.type)) return false;
    // List items are <p> blocks carrying listStyleType, rendered through the
    // custom BlockList wrapper (not Plate's standard list nodes). Nesting the
    // draggable container around them risks breaking the li > p / ::marker
    // layout the one-pager relies on, so they are not individually draggable —
    // surrounding non-list blocks still are. Re-enable once a browser pass
    // confirms list rendering survives the wrapper.
    if (element.listStyleType != null) return false;
    return true;
}
